import { useState } from 'react';
import Modal from '../../../components/Utils/ModalShow'; // added
import TableList from '../../../components/Tools/TableList';
import { FaFileAlt, FaCalendarAlt, FaTimesCircle, FaEdit, FaHistory, FaPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import RecapCard from "../../../components/Tools/RecapCard";
import { apiService } from '../../../components/Config/api.service';         // added
import { notify } from '../../../components/Utils/Notifications';           // added

const ActionCard = ({ title, description, buttonText, icon, onClick, buttonColor = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-[--color-primary] hover:bg-blue-100",
    red: "bg-red-50 text-red-600 hover:bg-red-100",
    green: "bg-green-50 text-green-600 hover:bg-green-100"
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 h-full flex flex-col justify-between">
      <div>
        <h3 className="text-lg font-bold mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-4">{description}</p>
      </div>
      <button 
        onClick={onClick}
        className={`px-4 py-2 rounded-lg ${colorClasses[buttonColor]} font-medium transition-all flex items-center justify-center gap-2`}
      >
        {buttonText}
      </button>
    </div>
  );
};

const formatCFA = (amount) => {
  if (!amount && amount !== 0) return "______";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const InfoSection = ({ title, fields, onUpdate }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 h-full">
    <div className="flex justify-between items-center mb-4">
      <h3 className="font-bold flex items-center">
        {title}
      </h3>
      <button
        onClick={onUpdate}
        className="px-3 py-1.5 bg-blue-50 text-[--color-primary] rounded-lg hover:bg-blue-100 transition-all text-sm font-medium flex items-center gap-1"
      >
        <FaEdit className="text-xs" /> Modifier
      </button>
    </div>
    <div className="grid grid-cols-1 gap-3">
    {fields.map((field, index) => (
      <div key={index} className="flex justify-between py-2 border-b border-gray-100">
        <span className="text-gray-500">{field.label}:</span>
        {field.isLink ? (
          <button 
            onClick={field.onClick}
            className="text-[--color-primary] font-medium hover:underline flex items-center gap-1"
          >
            {field.value} <FaChevronDown className="text-xs" />
          </button>
        ) : (
          <span className="font-medium">
            {field.isCurrency ? formatCFA(field.value) : (field.value || "______")}
          </span>
        )}
  </div>
))}
    </div>
  </div>
);

const Contrats = ({ handleDeleteFinalSettlement, finalSettlementRecap, handleDeleteLeaveDeparture, leaveDepartureRecap ,contracts = [], setActiveTab, currentContract, employee, externalContract = null, primes = [], setIsDrawerOpenContracts, setIsDrawerOpenExternalContracts, setIsDrawerOpenInformations, refreshContractInfo }) => { // refreshContractInfo prop added

  const columns = [
    { key: 'Ref', label: 'Name' },
    { key: 'Employee', label: 'Employee' },
    { key: 'DateStart', label: 'Start Date' },
    { key: 'DateEnd', label: 'End Date' },
    { 
      key: 'Statut', 
      label: 'Statut',
      render: (row) => {
          let badgeClass = '';
          let statusText = '';
          
          switch(row.Statut?.toLowerCase()) {
              case 'open':
                  badgeClass = 'badge-success';
                  statusText = 'En cours';
                  break;
              case 'close':
                  badgeClass = 'badge-error';
                  statusText = 'Expiré';
                  break;
              case 'draft':
                  badgeClass = 'badge-info';
                  statusText = 'Nouveau';
                  break;
              default:
                  badgeClass = 'badge-neutral';
                  statusText = row.Statut || 'N/A';
          }
          
          return <span className={`badge ${badgeClass}`}>{statusText}</span>;
      }
    },
  ];
  const [showAllPrimes, setShowAllPrimes] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false); // added
  const [isEndModalOpen, setIsEndModalOpen] = useState(false); // confirmation modal state

  // end external contract action
  const endExternalContract = () => {
    if (!externalContract?.id) return;
    setIsEndModalOpen(true);
  };
  
  const confirmEndExternalContract = async () => {
    if (!externalContract?.id) return;
    setIsProcessing(true);
    try {
      await apiService.updateElementById('/external_contract', externalContract.id, { state: 'expired' });
      notify('Contrat externe terminé', '', 2);
      if (typeof refreshContractInfo === 'function') refreshContractInfo();
    } catch (err) {
      console.error('Erreur lors de la fin du contrat externe', err);
      notify('Erreur lors de la fin du contrat externe', '', 3);
    } finally {
      setIsProcessing(false);
      setIsEndModalOpen(false);
    }
  };

  const getPrimeFields = () => {
    const baseFields = [];
    
    // Ajouter les deux premières primes si pas déplié
    if (!showAllPrimes) {
      return [
        ...baseFields,
        ...primes.slice(0, 2).map(prime => ({ 
          label: prime.name || "Prime", 
          value: prime.employeeAmount || prime.baseAmount, 
          isCurrency: true 
        })),
        // Bouton "Voir plus" seulement s'il y a plus de 2 primes
        ...(primes.length > 2 ? [{ 
          label: "Voir plus", 
          value: `${primes.length - 2} autres primes`, 
          isLink: true,
          onClick: () => setShowAllPrimes(true)
        }] : [])
      ];
    } else {
      // Montrer toutes les primes si déplié
      return [
        ...baseFields,
        ...primes.map(prime => ({ 
          label: prime.name || "Prime", 
          value: prime.employeeAmount || prime.baseAmount, 
          isCurrency: true 
        })),
        // Bouton pour réduire la liste
        { 
          label: "Réduire", 
          value: "", 
          isLink: true,
          onClick: () => setShowAllPrimes(false)
        }
      ];
    }
  };
  
  const openContractDrawer = () => { 
    setIsDrawerOpenContracts(true); 
  };

  const openExternalContractDrawer = () => {
    setIsDrawerOpenExternalContracts(true);
  };

  const openEmployeeDrawer = () => {
    setIsDrawerOpenInformations(true);
  }

  return (
    <div className="p-4 bg-gray-50">      
      {/* Employee Contract Banner */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between">
          <div className="mb-4 md:mb-0">
            <h2 className="text-xl font-bold mb-1">
              {currentContract ? `Contrat: ${currentContract.Ref || ''}` : 'Gestion de contrat'}
            </h2>
            <p className="text-gray-500">
              {currentContract
                ? `Contrat actif depuis le ${currentContract.DateStart || '-'}` 
                : (externalContract ? `Contrat externe actif depuis ${externalContract.date_start || '-'}` : "Aucun contrat actif pour cet employé")}
            </p>
          </div>

          {/* Right-side actions: add / show external / end external */}
          <div>
            {!currentContract && (
              externalContract ? (
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="px-3 py-2 bg-yellow-50 text-yellow-800 rounded-md text-sm">
                    Contrat externe actif — terminez-le pour pouvoir créer un contrat interne.
                  </div>
                  {/* <div className="flex items-center gap-2">
                    <button
                      onClick={openExternalContractDrawer}
                      className="px-4 py-2 bg-[--color-primary] text-white rounded-lg transition-all font-medium flex items-center gap-2 text-sm"
                    >
                      Voir / Modifier
                    </button>
                    <button
                      onClick={endExternalContract}
                      disabled={isProcessing}
                      className={`px-4 py-2 rounded-lg text-white ${isProcessing ? 'bg-gray-300' : 'bg-red-600 hover:bg-red-700'} text-sm`}
                    >
                      {isProcessing ? 'Traitement...' : 'Terminer le contrat externe'}
                    </button>
                  </div> */}
                </div>
              ) : (
                <button
                  onClick={() => openContractDrawer()}
                  className="px-4 py-2 bg-[--color-primary] text-white rounded-lg hover:bg-[--color-primary] transition-all font-medium flex items-center gap-2"
                >
                  <FaPlus size={14} /> Ajouter un contrat
                </button>
              )
            )}
          </div>
        </div>
      </div>
      {currentContract ? (
        <div className="space-y-6">
          {/* Action Cards */}
          <div className={` grid grid-cols-1 gap-3 ${(finalSettlementRecap || leaveDepartureRecap) ? 'mb-6 md:grid-cols-2' : 'md:grid-cols-3'}`}>
            <ActionCard 
              title="Générer un document"
              description="Générer un contrat ou autre document lié à cet employé."
              buttonText="Générer un document"
              icon={<FaFileAlt size={24} className="text-[--color-primary]" />}
              onClick={() => {}}
              buttonColor="blue"
            />
            
            {!leaveDepartureRecap && (
            <ActionCard 
              title="Départ en congés"
              description="Configurer le départ en congés pour cet employé."
              buttonText="Configurer les congés"
              icon={<FaCalendarAlt size={24} className="text-green-600" />}
              onClick={() => setActiveTab("LeaveDeparture")}
              buttonColor="green"
            />            
            )}

            {!finalSettlementRecap && (  
              <ActionCard 
                title="Fin de contrat"
                description="Démarrer la procédure de fin de contrat pour cet employé."
                buttonText="Configurer la fin"
                icon={<FaTimesCircle size={24} className="text-red-600" />}
                onClick={() => setActiveTab("FinalSettlement")}
                buttonColor="red"
              />
            )}
          </div>

          {leaveDepartureRecap && (
            <RecapCard
              icon={<FaCalendarAlt size={28} className="text-[var(--color-primary)]" />}              
              title="Récapitulatif du départ en congés"              
              state={leaveDepartureRecap.state}
              stateLabel={leaveDepartureRecap.state === "draft" ? "Brouillon" : "Validé"}
              fields={[
                { label: "Date de départ :", value: leaveDepartureRecap.date_depart },
                { label: "Date de retour :", value: leaveDepartureRecap.date_retour },
              ]}
              info={
                leaveDepartureRecap.state === "draft"
                  ? "Récapitulatif du départ en congés sauvegardé."
                  : "Récapitulatif du départ en congés validé."
              }
              onEdit={() => setActiveTab("LeaveDeparture")}
              onDelete={handleDeleteLeaveDeparture}
              showDelete={leaveDepartureRecap.state === "draft"}              
            />
          )}
          
          {finalSettlementRecap && (
            <RecapCard
              icon={<FaHistory size={28} className="text-[var(--color-primary)]" />}
              title="Récapitulatif de la fin de contrat"
              state={finalSettlementRecap.state}              
              stateLabel={finalSettlementRecap.state === "draft" ? "Brouillon" : "Validé"}
              fields={[
                { label: "Motif :", value: finalSettlementRecap.departure_reason_name },
                { label: "Date de sortie :", value: finalSettlementRecap.exit_date },
                {
                  label: "Solde à verser :",
                  value: (
                    <span className="font-bold text-xl ml-2 text-[var(--color-primary)]">
                      {Number(finalSettlementRecap.total_amount).toLocaleString("fr-FR")} XOF
                    </span>
                  ),
                },
              ]}
              info={
                finalSettlementRecap.state === "draft"
                  ? "Récapitulatif de la fin de contrat sauvegardé."
                  : "Récapitulatif de la fin de contrat validé."
              }
              onEdit={() => setActiveTab("FinalSettlement")}
              onDelete={handleDeleteFinalSettlement}
              showDelete={finalSettlementRecap.state === "draft"}
            />
          )}
          
          {/* Contract Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InfoSection
              title="Informations personnelles"
              fields={[
                { label: "Date de naissance", value: employee?.birthday },
                { label: "Adresse", value: employee?.address },  
                { label: "Numéro de téléphone", value: employee?.phone },
                { label: "Email", value: employee?.email }
              ]}
              onUpdate={openEmployeeDrawer}
            />
            
            <InfoSection
              title="Paramètres du contrat"
              fields={[
                { label: "Type de contrat", value: currentContract?.contractType},
                { label: "Nombre de part IR", value: currentContract?.part_ir},
                { label: "Date de début", value: currentContract?.DateStart },
                { label: "Date de fin", value: currentContract?.DateEnd },
              ]}
              onUpdate={openContractDrawer}
            />
            
            <InfoSection
              title="Informations sur le salaire"
              fields={[
                { label: "Catégorie", value: currentContract?.category },
                { label: "Convention", value: currentContract?.convention },
                { label: "Salaire de base", value: currentContract?.base_salary, isCurrency: true },
                { label: "Sursalaire", value: currentContract?.sursalaire, isCurrency: true }
              ]}
              onUpdate={openContractDrawer}
            />
            
            <InfoSection
              title="Primes et Avantages"
              fields={getPrimeFields()}
              onUpdate={() => {
                window.location.href = "/primes";
              }}
            />
          </div>
        </div>
      ) : externalContract ? (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="max-w-3xl mx-auto flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-4 w-full md:w-2/3">
                <div className="bg-yellow-50 rounded-lg p-3 flex items-center justify-center">
                  <FaFileAlt size={28} className="text-yellow-600" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm text-gray-500">Contrat externe</div>
                  <div className="font-medium text-lg">{externalContract.name || "Contrat externe"}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-500">
                      {externalContract.type ? `${externalContract.type}` : ''}
                    </span>
                    {externalContract.state && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                        {externalContract.state}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    {externalContract.date_start || '—'} → {externalContract.date_end || '—'}
                  </div>
                  <div className="mt-2 font-semibold text-[--color-primary]">
                    {externalContract.net_salary ? new Intl.NumberFormat('fr-FR',{ style:'currency', currency:'XOF', maximumFractionDigits:0}).format(externalContract.net_salary) : '—'}
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 flex flex-col items-end gap-2 w-full md:w-1/3">
                <div className="text-xs text-gray-500 text-right mb-1">Actions</div>
                <div className="flex gap-2">
                  <button
                    onClick={openExternalContractDrawer}
                    className="px-3 py-2 bg-[--color-primary] text-white rounded-lg text-sm"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={endExternalContract}
                    disabled={isProcessing}
                    className={`px-3 py-2 rounded-lg text-white ${isProcessing ? 'bg-gray-300' : 'bg-red-600 hover:bg-red-700'} text-sm`}
                  >
                    {isProcessing ? 'Traitement...' : 'Terminer'}
                  </button>
                </div>
                <div className="text-xs text-gray-400 mt-2">Vous devez terminer ce contrat externe pour pouvoir créer un contrat interne.</div>
              </div>
            </div>
            {/* Confirmation modal for ending external contract */}
            <Modal
              isOpen={isEndModalOpen}
              onClose={() => setIsEndModalOpen(false)}
              title="Terminer le contrat externe"
              subtitle={`Contrat: ${externalContract?.name || '—'}`}
              size={480}
              footer={
                <>
                  <button onClick={() => setIsEndModalOpen(false)} className="px-4 py-2 rounded bg-gray-100">Annuler</button>
                  <button onClick={confirmEndExternalContract} disabled={isProcessing} className="px-4 py-2 rounded bg-red-600 text-white">
                    {isProcessing ? 'Traitement...' : 'Confirmer la fin'}
                  </button>
                </>
              }
            >
              <p>Confirmez-vous la fin du contrat externe pour cet employé ?</p>
              <p className="text-sm text-gray-500 mt-3">Une fois terminé, vous pourrez ajouter un contrat interne.</p>
            </Modal>
          </div>
           ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center mb-6">
            <div className="bg-gray-50 h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaFileAlt size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">Aucun contrat en cours</h3>
            <p className="text-gray-500 mb-6 max-w-lg mx-auto">
              Cette personne n'a pas de contrat en cours. Vous pouvez lui ajouter ou créer un nouveau contrat.
            </p>
            <div className='flex justify-center gap-4'>
              <button
                onClick={openContractDrawer}
                className="px-5 py-2.5 bg-[--color-primary] text-white rounded-lg transition-all font-medium flex items-center gap-2 mx-auto"
              >
                <FaPlus size={14} /> Ajouter un contrat
              </button>

              <button
                onClick={openExternalContractDrawer}
                className="px-5 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all font-medium flex items-center gap-2 mx-auto"
              >
                <FaPlus size={14} /> Stagiaire/Prestataire
              </button>
            </div>
          </div>
        )}

      {/* Contract History */}
      <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold flex items-center">
            <span className="w-2 h-6 bg-purple-600 rounded-sm mr-2"></span>
            Historique des Contrats
          </h3>
          <button
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all text-sm font-medium flex items-center gap-2"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? (
              <>
                <FaChevronUp size={14} /> Masquer l'historique
              </>
            ) : (
              <>
                <FaChevronDown size={14} /> Voir l'historique
              </>
            )}
          </button>
        </div>
        
        {showHistory && (
          contracts.length > 0 ? (
            <div className="overflow-x-auto">
              <TableList columns={columns} data={contracts} searchDisplay={false} />
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <FaHistory size={32} className="text-gray-300 mx-auto mb-3" />
              <p>Aucun historique de contrat disponible</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Contrats;